<script lang="ts">
	import CardBadge from '$lib/components/ui/CardBadge.svelte';
	import ImageHoverGallery from '$lib/components/ui/ImageHoverGallery.svelte';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import { Importance } from '$lib/features/_other/importance.enum';
	import type { CityFeaturePreview } from '$lib/features/cityFeature/cityFeature.type';
	import { CITY_FEATURE_TYPE_ICON_MAP } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { NavLink } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import IconArrowRight from '~icons/mdi/arrow-right';
	import IconSchool from '~icons/mdi/school';
	import IconStarFourPoints from '~icons/mdi/star-four-points';

	const {
		elementId,
		feature,
		language,
		nav,
		badge,
		fact,
		isReversed, // isReversed = true puts image on the right.
		lazyLoadImage
	}: {
		elementId: string;
		feature: CityFeaturePreview;
		language: Language;
		nav: NavLink;
		badge?: { text: string; Icon: Component | undefined };
		fact: string | null;
		isReversed: boolean;
		lazyLoadImage: boolean;
	} = $props();
</script>

<article id={elementId} class="flex scroll-mt-20 flex-col items-stretch gap-8 md:flex-row">
	<div class="w-full flex-1 md:w-1/2 {isReversed ? 'md:order-2' : 'md:order-1'}">
		{@render ImageHalf()}
	</div>

	<div
		class={{
			'flex w-full flex-1 flex-col justify-center gap-6 py-4 md:w-1/2': true,
			'md:order-1 md:items-end md:text-right': isReversed,
			'md:order-2 md:items-start md:text-left': !isReversed
		}}
	>
		<h2>
			<a
				class="text-4xl transition-colors hover:text-primary md:text-5xl"
				href={`/${nav.href}`}
				data-sveltekit-preload-data={nav.preloadData}
				data-sveltekit-preload-code={nav.preloadCode}
			>
				{feature.englishName}
			</a>

			{#if feature.nativeName}
				<div class="mt-2">
					<i>
						<NativeText
							classes="text-sm text-base-content/70"
							languageCode={language.code}
							isLanguageRTL={language.rtl}
							text={feature.nativeName}
						/>
					</i>
				</div>
			{/if}
		</h2>

		<p class="w-full text-sm leading-relaxed font-light text-base-content/70 lg:text-lg">
			{feature.description}
		</p>

		{#if fact}
			<div class="relative">
				{@render Fact()}
			</div>
		{/if}

		{@render Action()}
	</div>
</article>

{#snippet ImageHalf()}
	<a
		class="group relative block h-[40svh] w-full overflow-hidden rounded-box bg-base-200 shadow-md transition-all duration-500 hover:-translate-y-1
               hover:shadow-xl md:h-full md:min-h-100"
		href={`/${nav.href}`}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
	>
		{#if feature.images.length > 0}
			<ImageHoverGallery images={feature.images} eagerLoading={!lazyLoadImage} />
		{:else}
			{@render NoImagePlaceholder()}
		{/if}

		<div class="absolute top-4 left-4 inline-flex gap-4">
			{#if feature.importance === Importance.ESSENTIAL}
				<CardBadge text="MUST SEE" Icon={IconStarFourPoints} />
			{/if}

			{#if badge}
				<CardBadge text={`${badge.text}`} Icon={badge.Icon} />
			{/if}
		</div>
	</a>

	{#snippet NoImagePlaceholder()}
		{const Icon = $derived<Component>(CITY_FEATURE_TYPE_ICON_MAP[feature.type])}
		<div
			class="absolute inset-0 flex flex-col items-center justify-center bg-base-200 text-base-content/20 transition-colors group-hover:text-primary/40"
		>
			<Icon class="h-24 w-24" />
		</div>
	{/snippet}
{/snippet}

{#snippet Fact()}
	<div class={{ 'absolute top-0 bottom-0 w-1 rounded-box bg-info/25': true, 'right-0': isReversed, 'left-0': !isReversed }}></div>
	<div class={{ 'pr-6': isReversed, 'pl-6': !isReversed }}>
		<p class="inline-flex items-center gap-2 text-sm font-bold tracking-wider text-base-content/70">
			<IconSchool />
			Did you know?
		</p>

		<div role="alert" class="alert alert-soft text-xs alert-info">
			{fact}
		</div>
	</div>
{/snippet}

{#snippet Action()}
	<a
		class="group btn w-full rounded-full shadow-md btn-primary font-mono"
		href={`/${nav.href}`}
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
	>
		view {feature.englishName} guide
		<IconArrowRight class="transition-transform group-hover:translate-x-1" />
	</a>
{/snippet}
